import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import DataTable from "react-data-table-component";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import LogDetailsModal from "../../components/common/LogDetailsModal";
import { fetchFormLogs, fetchFormsList } from "../../store/slices/logSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "36px",
    height: "36px",
    fontSize: "0.875rem",
    borderRadius: "0.375rem",
    borderColor: "#d9dee3",
    "&:hover": { borderColor: "#d9dee3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "36px" }),
  input: (base) => ({ ...base, margin: "0", padding: "0" }),
  indicatorsContainer: (base) => ({ ...base, height: "36px" }),
  menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 9999 }),
};

const FormLogs = () => {
  const dispatch = useDispatch();
  const { formLogs, formLogsPagination, formsList, usersList, isLoading } = useSelector(
    (state) => state.logs
  );

  const [showFilters, setShowFilters] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [filters, setFilters] = useState({
    form_key: "",
    user_id: "",
    status: "",
    start_date: "",
    end_date: "",
    search: "",
    page: 1,
    per_page: 15,
  });

  useEffect(() => {
    dispatch(fetchFormsList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchFormLogs(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      form_key: "",
      user_id: "",
      status: "",
      start_date: "",
      end_date: "",
      search: "",
      page: 1,
      per_page: 15,
    });
  };

  const formOptions = [
    { value: "", label: "All Form Types" },
    ...(formsList || []).map((f) => ({ value: f.key, label: f.label })),
  ];

  const userOptions = [
    { value: "", label: "All Users" },
    ...(usersList || []).map((u) => ({
      value: u.id,
      label: `${u.name || u.email} (${u.email})`,
    })),
  ];

  const formatJSON = (val) => {
    if (!val) return "—";
    try {
      const parsed = typeof val === "string" ? JSON.parse(val) : val;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(val);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const columns = [
    {
      name: "S.No",
      cell: (row, index) =>
        (formLogsPagination.current_page - 1) * formLogsPagination.per_page + index + 1,
      width: "70px",
    },
    {
      name: "User",
      selector: (row) => row.user?.name || "System / Guest",
      cell: (row) => (
        <div>
          <div className="fw-semibold text-dark">
            {row.user?.name || "Guest/System"}
          </div>
          <small className="text-muted">{row.user?.email || "N/A"}</small>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Function / Action",
      selector: (row) => row.function || "",
      cell: (row) => <span className="badge bg-label-info">{row.function || "N/A"}</span>,
      sortable: true,
      width: "150px",
    },
    {
      name: "Form URL",
      selector: (row) => row.url || "",
      cell: (row) => (
        <span className="text-truncate" title={row.url} style={{ maxWidth: "240px", fontSize: "0.8125rem" }}>
          {row.url || "—"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "",
      cell: (row) => {
        const isSuccess = row.status === "success";
        return (
          <span className={`badge ${isSuccess ? "bg-label-success" : "bg-label-danger"}`}>
            <i className={`${isSuccess ? "ri-checkbox-circle-line" : "ri-error-warning-line"} me-1`}></i>
            {row.status ? row.status.toUpperCase() : "UNKNOWN"}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "IP Address",
      selector: (row) => row.user_ip || "—",
      width: "130px",
    },
    {
      name: "Timestamp",
      selector: (row) => row.log_date || "",
      cell: (row) => <small className="text-muted">{formatDateTime(row.log_date)}</small>,
      sortable: true,
      width: "170px",
    },
    {
      name: "Details",
      cell: (row) => (
        <button
          className="btn btn-xs btn-outline-primary"
          onClick={() => {
            setSelectedLog(row);
            setShowModal(true);
          }}
        >
          <i className="ri-file-search-line me-1"></i> Inspect
        </button>
      ),
      width: "110px",
    },
  ];

  return (
    <MainLayout>
      <Breadcrumbs />
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-0 text-dark">Form Logs</h4>
            <small className="text-muted">Form activity audit trail and payload inspection</small>
          </div>
          <div>
            <button
              className={`btn btn-sm ${showFilters ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="ri-filter-3-line me-1"></i> {showFilters ? "Hide Filters" : "Filters"}
            </button>
          </div>
        </div>

        {/* Filters Box */}
        {showFilters && (
          <div className="px-4 py-3 border-bottom bg-light">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label small fw-bold">Form Type</label>
                <Select
                  options={formOptions}
                  styles={selectStyles}
                  value={formOptions.find((opt) => opt.value === filters.form_key)}
                  onChange={(opt) => handleFilterChange("form_key", opt ? opt.value : "")}
                  placeholder="All Form Types"
                  isSearchable
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold">User</label>
                <Select
                  options={userOptions}
                  styles={selectStyles}
                  value={userOptions.find((opt) => opt.value === filters.user_id)}
                  onChange={(opt) => handleFilterChange("user_id", opt ? opt.value : "")}
                  placeholder="All Users"
                  isSearchable
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold">Date From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold">Date To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-bold">Search Keywords</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search message, URL, payload, or user..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>
              <div className="col-md-6 d-flex align-items-end justify-content-end gap-2">
                <button className="btn btn-sm btn-label-secondary" onClick={handleResetFilters}>
                  <i className="ri-refresh-line me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table Body */}
        <div className="card-body p-2">
          <DataTable
            columns={columns}
            data={formLogs}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationTotalRows={formLogsPagination.total}
            paginationPerPage={filters.per_page}
            paginationDefaultPage={filters.page}
            onChangePage={(page) => setFilters((prev) => ({ ...prev, page }))}
            onChangeRowsPerPage={(perPage, page) =>
              setFilters((prev) => ({ ...prev, per_page: perPage, page }))
            }
            highlightOnHover
            striped
            responsive
            noDataComponent={
              <div className="p-4 text-center text-muted">
                <i className="ri-information-line ri-2x mb-2"></i>
                <div>No form logs found for the selected criteria.</div>
              </div>
            }
          />
        </div>
      </div>

      {/* Log Inspection Modal */}
      <LogDetailsModal
        show={showModal}
        log={selectedLog}
        onClose={() => {
          setShowModal(false);
          setSelectedLog(null);
        }}
      />
    </MainLayout>
  );
};

export default FormLogs;
